import {
  DASHBOARD_MESSAGE_CONTACTS,
  DASHBOARD_MESSAGE_THREADS,
  getDefaultDashboardCurrentUser,
} from "../messages.mock";
import type {
  DashboardAttachment,
  DashboardMessage,
  DashboardParticipant,
  DashboardThread,
} from "../types.js";
import type {
  DashboardDraftAttachment,
  DashboardMessageComposerSubmit,
  DashboardMessageContact,
} from "../messages.types.js";

function normalizeQuery(value: string): string {
  return value.trim().toLowerCase();
}

function buildThreadSearchIndex(thread: DashboardThread): string {
  return normalizeQuery(
    [
      thread.title,
      thread.contextLabel,
      thread.snippet,
      thread.updatedAtLabel,
      thread.participantsLabel,
      ...thread.participants.map((participant) => participant.name),
      ...thread.messages.map((message) => message.body),
    ].join(" "),
  );
}

function getMessageSortValue(message: DashboardMessage): number {
  if (!message.sentAtIso) {
    return 0;
  }

  const time = new Date(message.sentAtIso).getTime();
  return Number.isFinite(time) ? time : 0;
}

function getThreadSortValue(thread: DashboardThread): number {
  if (thread.updatedAtIso) {
    const value = new Date(thread.updatedAtIso).getTime();
    if (Number.isFinite(value)) {
      return value;
    }
  }

  return thread.messages.reduce(
    (latestTime, message) => Math.max(latestTime, getMessageSortValue(message)),
    0,
  );
}

function cloneAttachment(attachment: DashboardAttachment): DashboardAttachment {
  return { ...attachment };
}

function cloneMessage(message: DashboardMessage): DashboardMessage {
  return {
    ...message,
    attachment: message.attachment ? cloneAttachment(message.attachment) : undefined,
    attachments: message.attachments?.map(cloneAttachment),
  };
}

function cloneParticipant(participant: DashboardParticipant): DashboardParticipant {
  return { ...participant };
}

function cloneThread(thread: DashboardThread): DashboardThread {
  return {
    ...thread,
    participants: thread.participants.map(cloneParticipant),
    messages: thread.messages.map(cloneMessage),
    attachment: thread.attachment ? cloneAttachment(thread.attachment) : undefined,
  };
}

function sortThreads(threads: ReadonlyArray<DashboardThread>): DashboardThread[] {
  return [...threads].sort((left, right) => getThreadSortValue(right) - getThreadSortValue(left));
}

export function getSeededDashboardThreads(): DashboardThread[] {
  return DASHBOARD_MESSAGE_THREADS.map(cloneThread);
}

export function getSeededDashboardContacts(): DashboardMessageContact[] {
  return DASHBOARD_MESSAGE_CONTACTS.map((contact) => ({ ...contact }));
}

export function filterDashboardThreads(
  threads: ReadonlyArray<DashboardThread>,
  searchQuery: string,
): DashboardThread[] {
  const normalizedQuery = normalizeQuery(searchQuery);
  if (!normalizedQuery) {
    return sortThreads(threads);
  }

  return sortThreads(
    threads.filter((thread) => buildThreadSearchIndex(thread).includes(normalizedQuery)),
  );
}

export function getInitialDashboardThreadId(
  threads: ReadonlyArray<DashboardThread>,
  preferredThreadId?: string | null,
): string | null {
  if (preferredThreadId && threads.some((thread) => thread.id === preferredThreadId)) {
    return preferredThreadId;
  }

  return threads[0]?.id ?? null;
}

export function getDashboardThreadById(
  threads: ReadonlyArray<DashboardThread>,
  threadId: string | null | undefined,
): DashboardThread | null {
  if (!threadId) {
    return null;
  }

  return threads.find((thread) => thread.id === threadId) ?? null;
}

export function filterDashboardContacts(
  contacts: ReadonlyArray<DashboardMessageContact>,
  searchQuery: string,
): DashboardMessageContact[] {
  const normalizedQuery = normalizeQuery(searchQuery);
  if (!normalizedQuery) {
    return [...contacts];
  }

  return contacts.filter((contact) =>
    normalizeQuery(
      [contact.name, contact.email, contact.companyLabel, contact.roleLabel].join(" "),
    ).includes(normalizedQuery),
  );
}

function getAttachmentKindFromFile(file: File): DashboardAttachment["kind"] {
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  if (["dwg", "dxf", "step", "stp", "igs", "iges", "sldprt", "sldasm", "ipt", "iam", "catpart", "catproduct", "x_t", "x_b"].includes(extension)) {
    return "cad";
  }

  if (["glb", "gltf", "obj", "fbx", "stl"].includes(extension)) {
    return "model";
  }

  if (file.type.startsWith("image/")) {
    return "image";
  }

  if (file.type.startsWith("video/")) {
    return "video";
  }

  return extension === "pdf" || file.type === "application/pdf" ? "pdf" : "image";
}

function formatFileSize(sizeInBytes: number): string {
  if (!Number.isFinite(sizeInBytes) || sizeInBytes <= 0) {
    return "0 KB";
  }

  const units = ["KB", "MB", "GB"];
  let value = sizeInBytes / 1024;
  let index = 0;
  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }

  return `${value >= 10 ? value.toFixed(0) : value.toFixed(1)} ${units[index]}`;
}

export function createDraftAttachmentFromFile(file: File): DashboardDraftAttachment {
  const kind = getAttachmentKindFromFile(file);

  return {
    file,
    id: `draft-${file.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${file.lastModified.toString(36)}`,
    kind,
    name: file.name,
    sizeLabel: formatFileSize(file.size),
    metaLabel:
      kind === "cad"
        ? "CAD drawing"
        : kind === "model"
          ? "3D model"
          : kind === "pdf"
            ? "PDF document"
            : kind === "video"
              ? "Video clip"
              : "Image asset",
  };
}

function draftAttachmentToMessageAttachment(
  attachment: DashboardDraftAttachment,
  updatedAtLabel: string,
): DashboardAttachment {
  return {
    id: attachment.id,
    kind: attachment.kind,
    metaLabel: attachment.metaLabel,
    name: attachment.name,
    sizeLabel: attachment.sizeLabel,
    updatedAtLabel,
  };
}

export function createOutgoingDashboardMessage(
  submission: DashboardMessageComposerSubmit,
  sentAt: Date = new Date(),
): DashboardMessage {
  const currentUser = getDefaultDashboardCurrentUser();
  const sentAtLabel = sentAt.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
  const attachments = submission.attachments.map((attachment) =>
    draftAttachmentToMessageAttachment(attachment, sentAtLabel),
  );

  return {
    id: `message-${sentAt.getTime().toString(36)}`,
    senderId: currentUser.id,
    sender: currentUser.name,
    sentAtLabel,
    sentAtIso: sentAt.toISOString(),
    body: submission.body.trim(),
    direction: "outbound",
    status: "delivered",
    attachment: attachments[0],
    attachments,
  };
}

export function appendMessageToThread(
  threads: ReadonlyArray<DashboardThread>,
  threadId: string,
  nextMessage: DashboardMessage,
): DashboardThread[] {
  return sortThreads(
    threads.map((thread) => {
      if (thread.id !== threadId) {
        return thread;
      }

      return {
        ...thread,
        snippet: nextMessage.body || nextMessage.attachments?.[0]?.name || thread.snippet,
        updatedAtLabel: nextMessage.sentAtLabel,
        updatedAtIso: nextMessage.sentAtIso,
        unreadCount: 0,
        attachment:
          nextMessage.attachments?.[0] ??
          nextMessage.attachment ??
          thread.attachment,
        messages: [...thread.messages, nextMessage],
      };
    }),
  );
}

export function upsertDashboardThread(
  threads: ReadonlyArray<DashboardThread>,
  nextThread: DashboardThread,
): DashboardThread[] {
  const threadRecord = cloneThread(nextThread);
  const hasExistingThread = threads.some((thread) => thread.id === nextThread.id);

  if (!hasExistingThread) {
    return sortThreads([threadRecord, ...threads]);
  }

  return sortThreads(
    threads.map((thread) => (thread.id === nextThread.id ? threadRecord : thread)),
  );
}

export function openOrCreateThreadForContact(
  threads: ReadonlyArray<DashboardThread>,
  contact: DashboardMessageContact,
): { thread: DashboardThread; created: boolean } {
  if (contact.existingThreadId) {
    const existingThread = threads.find((thread) => thread.id === contact.existingThreadId);
    if (existingThread) {
      return { thread: existingThread, created: false };
    }
  }

  const currentUser = getDefaultDashboardCurrentUser();
  const now = new Date();
  const newThread: DashboardThread = {
    id: `thread-${contact.id}`,
    title: contact.name,
    contextLabel: contact.companyLabel ?? contact.roleLabel,
    snippet: "Conversation started.",
    updatedAtLabel: "Now",
    updatedAtIso: now.toISOString(),
    participantsLabel: contact.name,
    participants: [
      currentUser,
      {
        id: contact.id,
        name: contact.name,
        avatarLabel: contact.avatarLabel,
        accent: contact.accent,
        presence: contact.presence,
        roleLabel: contact.roleLabel,
      },
    ],
    messages: [],
  };

  return { thread: newThread, created: true };
}

export function getMessageDayLabel(message: DashboardMessage): string {
  if (!message.sentAtIso) {
    return "Recent";
  }

  const date = new Date(message.sentAtIso);
  if (!Number.isFinite(date.getTime())) {
    return "Recent";
  }

  const today = new Date();
  if (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  ) {
    return "Today";
  }

  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
  });
}
